import { Component, Suspense } from "react";
import Loader from "./components/utils/loader";
import Login from "./components/game/login/login";
import Game from "./components/game/game";
import Config from "./config";
import { SkeletonTheme } from "react-loading-skeleton";
import ScreenWakeLock from "./ScreenWakeLock";
import { requestFullScreen, exitFullScreen, handleFullScreenChange } from "./components/utils/requestFullScreen";
import rotateScreenIndicator from "./assets/images/screen.png";
import iphoneInstructions from "./assets/images/lobby/iphone_instructions.png";
import close_1 from './assets/images/table/close_1.svg';
import './i18N';

import "./css/app.css";

const deviceInfoCache = (() => {
    let cache = null;
    return () => {
        if (!cache) {
            const userAgent = navigator.userAgent;
            cache = {
                isIOS: /iPhone|iPad|iPod/i.test(userAgent),
                isMac: /Macintosh/i.test(userAgent) && !/iPhone|iPad/i.test(userAgent),
                isAndroid: /Android/i.test(userAgent),
                isWindows: /Windows/i.test(userAgent),
                isSafari: /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)
            };
        }
        return cache;
    };
})();

export default class App extends Component {
    constructor(props) {
        super(props);

        const deviceInfo = deviceInfoCache();

        const initialStageProps = this.calculateStageProperties();

        this.state = {
            showLoading: true,
            showLogin: false,
            loginResp1: "",
            loginResp2: "",
            error: true,
            isSessionActive: false,
            sid: "",
            BgOpacity: 1,
            loginBtnState: false,
            showLoader: false,
            stageProperties: initialStageProps,
            showInstallPrompt: false,
            isIOS: deviceInfo.isIOS,
            isMac: deviceInfo.isMac,
            isAndroid: deviceInfo.isAndroid,
            isWindows: deviceInfo.isWindows,
            isSafari: deviceInfo.isSafari,
            instantPlayUrl: false,
            fullScreenEnabled: false,
            isThisPWA: false,
            showIphoneInstructions: false,
            deferredPrompt: null,
            PWAInstallUrl: false
        };

        this.config = new Config();
        this.handleResize = this.throttle(this.handleResize.bind(this), 250);
        this.handleInstallClick = this.handleInstallClick.bind(this);
        this.resizeTimeout = null;
    }

    calculateStageProperties() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const userAgent = navigator.userAgent.toLowerCase();

        const isMobile = /mobile|iphone|ipod|android/i.test(userAgent);
        const isTablet = /tablet|ipad|android(?!.*mobile)/i.test(userAgent);
        const isDesktop = !isMobile && !isTablet;
        const orientation = width > height ? 'landscape' : 'portrait';

        let deviceType, deviceOrientation;

        if (isDesktop) {
            deviceType = 'desktop';
            deviceOrientation = null;
        } else if (isTablet) {
            deviceType = 'tablet';
            deviceOrientation = orientation;
        } else if (isMobile) {
            deviceType = 'mobile';
            deviceOrientation = orientation;
        } else {
            deviceType = 'unknown';
            deviceOrientation = null;
        }

        let stageWidth, stageHeight;

        if (
            (deviceType === "mobile" && deviceOrientation === "landscape") ||
            (deviceType === "tablet" && deviceOrientation === "landscape") ||
            (deviceType === "desktop")
        ) {
            if (deviceType === "mobile") {
                stageWidth = 800;
                stageHeight = 480;
            } else {
                stageWidth = 1600;
                stageHeight = 960;
            }
        } else {
            stageWidth = 550;
            stageHeight = 760;
        }

        return {
            width: stageWidth,
            height: stageHeight,
            deviceOrientation,
            deviceType
        };
    }

    throttle(func, limit) {
        let inThrottle;
        return function () {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    componentDidMount() {
        this.checkPWA();
        this.onAppMount();
        this.config.getDevice();
        this.checkForPWAInstall();
        this.checkForInstantPlay();
        this.setupInstallListener();

        if (this.state.isIOS || (this.state.isMac && this.state.isSafari)) {
            this.setState({ showInstallPrompt: true });
        }

        const isCurrentlyFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );

        this.setState({ fullScreenEnabled: !isCurrentlyFullscreen });

        this.fullScreenListenerCleanup = handleFullScreenChange((isFullscreen) => {
            if (!isFullscreen) {
                this.setState({ fullScreenEnabled: true });
            }
        });

        window.addEventListener('resize', this.handleResize);
        window.addEventListener('orientationchange', this.handleResize);
        document.addEventListener('visibilitychange', this.handleResize);
    }

    checkForPWAInstall = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const shouldInstall = urlParams.get('pwa-install') === 'true';
        this.setState({ PWAInstallUrl: shouldInstall });
        if (shouldInstall && 'serviceWorker' in navigator) {
            this.registerServiceWorker();
        }
    };

    checkForInstantPlay = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const checkStatus = urlParams.get('instant-play') === 'true';
        this.setState({
            instantPlayUrl: checkStatus,
            fullScreenEnabled: checkStatus
        });

        if (this.state.isIOS && checkStatus) {
            this.setState({ showIphoneInstructions: true });
        }
    };

    registerServiceWorker = () => {
        navigator.serviceWorker.register('/service-worker.js')
            .then((registration) => {
                console.log('ServiceWorker registration successful');
            })
            .catch((err) => {
                console.log('ServiceWorker registration failed: ', err);
            });
    };

    setupInstallListener = () => {
        const beforeInstallHandler = (e) => {
            e.preventDefault();
            this.setState({
                deferredPrompt: e,
                showInstallPrompt: true
            });
        };

        window.addEventListener('beforeinstallprompt', beforeInstallHandler);
        this.beforeInstallHandler = beforeInstallHandler;
    };

    handleInstallClick = () => {
        if (this.state.isIOS || (this.state.isMac && this.state.isSafari)) return;

        const { deferredPrompt } = this.state;
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then(choiceResult => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted install');
                }
                this.setState({ deferredPrompt: null, showInstallPrompt: false });
            });
        }
    };

    checkPWA = () => {
        const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
        const iosStandalone = window.navigator.standalone === true;

        const isPWA = this.state.isIOS ? iosStandalone : (this.state.isAndroid && standaloneMedia);

        this.setState({ isThisPWA: isPWA });
    };

    componentWillUnmount() {
        if (this.fullScreenListenerCleanup) {
            this.fullScreenListenerCleanup();
        }

        if (this.beforeInstallHandler) {
            window.removeEventListener('beforeinstallprompt', this.beforeInstallHandler);
        }

        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('orientationchange', this.handleResize);
        document.removeEventListener('visibilitychange', this.handleResize);

        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
    }

    handleResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            if (this.state.isIOS && this.state.instantPlayUrl) {
                this.setState({ showIphoneInstructions: true });
            }

            const newStageProperties = this.calculateStageProperties();

            if (JSON.stringify(newStageProperties) !== JSON.stringify(this.state.stageProperties)) {
                this.setState({ stageProperties: newStageProperties });
            }
        }, 100);
    }

    onFormSubmit(from) {
        if (from === 'login_err') {
            this.setState({ showLoader: false });
        } else {
            this.setState({
                loginResp1: "Please Wait...  Loggin In",
                loginResp2: "",
                BgOpacity: 0.5,
                loginBtnState: true,
                error: true,
                showLoader: true
            });
        }
    }

    logOutHandler() {
        this.setState({ showLoader: true });
        sessionStorage.clear();

        if (this.state.isIOS || this.state.isAndroid) {
            exitFullScreen();
        }

        setTimeout(() => {
            this.clearLocalStorageValues();
            this.setState({
                showLogin: true,
                showLoading: false,
                isSessionActive: false,
                sid: null,
                BgOpacity: 1,
                showLoader: false
            });
        }, 2000);
    }

    clearLocalStorageValues() {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith("profileBackground_")) {
                localStorage.removeItem(key);
            }
        }
    }

    AppHandler(action) {
        if (action) {
            this.setState({ showLoader: false });

            if (action.status) {
                this.setState({
                    showLogin: false,
                    showLoading: false,
                    isSessionActive: true,
                    sid: action.sid,
                    loginResp1: "",
                    loginResp2: "",
                    loginBtnState: false,
                    error: true
                });
            } else {
                this.setState({
                    showLogin: true,
                    showLoading: false,
                    loginResp1: "Oops!",
                    loginResp2: action.description,
                    loginBtnState: false,
                    BgOpacity: 1,
                    isSessionActive: false,
                    error: false
                });
            }
        }
    }

    onAppMount() {
        const sessionData = JSON.parse(sessionStorage.getItem(`${window.location.hostname}_sid`));
        if (sessionData) {
            this.setState({
                sid: sessionData.sid,
                showLogin: false,
                showLoading: false
            });
        } else {
            this.setState({
                showLogin: true,
                showLoading: false
            });
        }
    }

    handleFullScreenClick = () => {
        const { instantPlayUrl, isIOS, isAndroid } = this.state;
        if (instantPlayUrl && (isIOS || isAndroid)) {
            requestFullScreen();
            this.setState({ fullScreenEnabled: false });
        }
    };

    shouldShowInstallPrompt = () => {
        const { showInstallPrompt, PWAInstallUrl } = this.state;
        return showInstallPrompt && PWAInstallUrl;
    };

    shouldShowIphoneInstructions = () => {
        const { showIphoneInstructions } = this.state;
        return showIphoneInstructions;
    };

    shouldShowRotateScreen = () => {
        const { isThisPWA, isIOS, isAndroid, stageProperties } = this.state;
        return isThisPWA && (isIOS || isAndroid) && stageProperties.deviceOrientation !== "landscape";
    };


    render() {
        const {
            isIOS,
            isMac,
            isAndroid,
            isWindows,
            instantPlayUrl,
            fullScreenEnabled,
            isSafari,
            stageProperties,
            showLoader,
            showLoading,
            showLogin,
            sid
        } = this.state;

        const bgClass = (stageProperties.deviceOrientation === "landscape" || stageProperties.deviceOrientation === null)
            ? "AppLandscapeBg"
            : "AppPortraitBg";

        return (
            <div className={`${bgClass} App`}>
                <ScreenWakeLock />

                {(fullScreenEnabled && instantPlayUrl && isAndroid) && (
                    <div className="pwa-install-container">
                        <div className="pwa-install-popup">
                            <div className="pwa-install-content">
                                <h3>Please Enable Full Screen</h3>
                                <p className="benefit-text">Click the button below to enable full screen.</p>
                                <button style={{ width: "130px", margin: "auto" }} className="btn_2" onClick={this.handleFullScreenClick}>
                                    <i className="bi bi-fullscreen" style={{ marginRight: "8px" }}></i>
                                    Full Screen
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {this.shouldShowIphoneInstructions() && (
                    <div className="pwa-install-container">
                        <div className="pwa-install-popup" style={{ background: "#141412" }}>
                            <div className="pwa-install-content p_10">
                                <button type="button" className="close_1" onClick={() => this.setState({ showIphoneInstructions: false })}>
                                    <img src={close_1} alt="" />
                                </button>
                                <h3>Please Enable Full Screen</h3>
                                <p className="benefit-text">Follow the below instructions to enable full screen.</p>
                                <img className="instuctions" src={iphoneInstructions} alt="" />
                            </div>
                        </div>
                    </div>
                )}

                {this.shouldShowInstallPrompt() && (
                    <div className="pwa-install-container">
                        <div className="pwa-install-popup">
                            <div className="pwa-install-content">
                                <h3>🎮 Install Our Game App</h3>
                                <p className="benefit-text">Get the best experience with faster loading and home screen access!</p>

                                {(isAndroid || isWindows || (isMac && !isSafari)) && (
                                    <button style={{ width: "100%" }}
                                        id="pwa-install-button"
                                        className="btn_2"
                                        onClick={this.handleInstallClick}
                                        aria-label="Install game application"
                                    >
                                        <span className="install-icon">⬇️</span> Install Now
                                    </button>
                                )}

                                <div className="platform-instructions">
                                    {isWindows && (
                                        <div className="instruction windows-instruction">
                                            <span className="icon">🖥️</span> Adds to your <strong>Start Menu</strong>
                                        </div>
                                    )}

                                    {isMac && !isIOS && !isSafari && (
                                        <div className="instruction mac-instruction">
                                            <span className="icon">💻</span> Adds to your <strong>Dock</strong>
                                        </div>
                                    )}

                                    {isMac && isSafari && (
                                        <div className="instruction mac-instruction">
                                            <ol>
                                                <li>Click <strong>File</strong> in menu bar</li>
                                                <li>Select <strong>"Add to Dock"</strong></li>
                                            </ol>
                                        </div>
                                    )}

                                    {isIOS && (
                                        <div className="instruction ios-instruction">
                                            <p><span className="icon">📱</span> Follow the steps below to <strong>install PWA</strong>:</p>
                                            <ol>
                                                <li>
                                                    Tap on
                                                    <span className="icon">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            height="24"
                                                            viewBox="0 -960 960 960"
                                                            width="24"
                                                            fill="#4287f5"
                                                            aria-hidden="true"
                                                        >
                                                            <path d="M240-40q-33 0-56.5-23.5T160-120v-440q0-33 23.5-56.5T240-640h120v80H240v440h480v-440H600v-80h120q33 0 56.5 23.5T800-560v440q0 33-23.5 56.5T720-40H240Zm200-280v-447l-64 64-56-57 160-160 160 160-56 57-64-64v447h-80Z" />
                                                        </svg>
                                                    </span>
                                                </li>
                                                <li>
                                                    Select <strong>"Add to Home Screen"</strong>
                                                    <span className="icon">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            height="24"
                                                            viewBox="0 -960 960 960"
                                                            width="24"
                                                            fill="#4287f5"
                                                            aria-hidden="true"
                                                        >
                                                            <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm360-360v160h-80v-160H280v-80h160v-160h80v160h160v80H560Z" />
                                                        </svg>
                                                    </span>
                                                </li>
                                                <li>Enter a display name as Kingsroom and click on Add</li>
                                            </ol>
                                        </div>
                                    )}

                                    {isAndroid && (
                                        <div className="instruction android-instruction">
                                            <span className="icon">📱</span> Works offline after installation
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {this.shouldShowRotateScreen() && (
                    <div className="screen_rotate_box">
                        <img src={rotateScreenIndicator} alt="" />
                        <p>Please rotate your phone to continue...</p>
                    </div>
                )}

                {showLoader && <Loader />}

                <Suspense fallback={<Loader />}>
                    {!showLoading && showLogin && (
                        <Login
                            className="animateComponent"
                            stageProperties={stageProperties}
                            handler={this.AppHandler.bind(this)}
                            onSubmit={this.onFormSubmit.bind(this)}
                            loginBtnState={this.state.loginBtnState}
                        />
                    )}
                    {!showLoading && !showLogin &&
                        <SkeletonTheme baseColor="#313131" highlightColor="#525252">
                            <Game sid={sid} stageProperties={stageProperties} logOutHandler={this.logOutHandler.bind(this)} />
                        </SkeletonTheme>
                    }
                </Suspense>
            </div>
        );
    }
}